/* eslint-disable jira/filename-case */
// Stryker disable all

import { retryOnError } from '@atlassian/relay-fetch/retry-on-error';
import type { RetryOptions } from '@atlassian/relay-fetch/retry-options';
import {
	BAD_REQUEST,
	CONTENT_TOO_LARGE,
	NO_CONTENT,
} from '@jira/platform__http-status-codes/src/http-status-codes.tsx';
import killswitch from '@jira/platform__utils-feature-gates-killswitch/src/index.tsx';
import HttpError, { type FieldValidationError, ValidationError } from './errors.tsx';
import { defaultOptions as generalOptions } from './fetch-default-options.tsx';
import { getReroutableURL } from './get-reroutable-url.tsx';
import { applyObservabilityHeaders } from './observability-headers.tsx';
import { getTraceId } from './trace-id.tsx';

/**
 * Transform object with field errors from JIRA to an array of FieldValidationErrors
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const transformFieldErrorsFromServer = (fieldErrors: Record<any, any>): FieldValidationError[] =>
	Object.keys(fieldErrors).map((field) => ({
		field,
		error: fieldErrors[field],
	}));

/**
 * Transforms validation errors we receive from server
 */
export const transformValidationErrorFromServer = (
	json: {
		errorMessages?: string[];
		errors?: Record<string, string[]>;
		fieldErrors?: Record<string, string[]>;
	} & Record<string, unknown>,
	statusCode?: number,
	traceId?: string,
): ValidationError => {
	const message = json.errorMessages ? json.errorMessages.join('; ') : 'validation failed';
	const fieldErrorsJson = json.errors ?? json.fieldErrors;
	const fieldErrors = fieldErrorsJson ? transformFieldErrorsFromServer(fieldErrorsJson) : [];
	return new ValidationError(message, fieldErrors, statusCode, traceId, json);
};

const putOptions = {
	...generalOptions,
	method: 'PUT',
} as const;

const postOptions = {
	...generalOptions,
	method: 'POST',
} as const;

const deleteOptions = {
	...generalOptions,
	method: 'DELETE',
} as const;

const patchOptions = {
	...generalOptions,
	method: 'PATCH',
} as const;

export function withErrorHandling(source: Promise<Response>): Promise<Response> {
	return source.then(handleErrors).catch(handleNetworkErrors);
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const handleNetworkErrors = (err: Error): Promise<any> => {
	if (err instanceof TypeError) {
		throw new Error('Failed to fetch', { cause: err });
	}
	throw err;
};

export const handleErrors = (response: Response): Response | Promise<Response> => {
	const traceId = getTraceId(response);
	// CONTENT_TOO_LARGE errorMessage is used for Hard Limits and, we don't want to override the error message
	if (BAD_REQUEST === response.status || CONTENT_TOO_LARGE === response.status) {
		return response.json().then((errorContent) => {
			throw transformValidationErrorFromServer(errorContent, response.status, traceId);
		});
	}
	if (!response.ok) {
		if (traceId !== undefined && traceId.length > 0) {
			throw new HttpError(
				response.status,
				`Error server response: ${response.status}`,
				traceId,
				response,
			);
		} else {
			throw new HttpError(
				response.status,
				`Error server response: ${response.status}`,
				undefined,
				response,
			);
		}
	}
	return response;
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const processResponseFromServer = (response: Response): Promise<any> => {
	if (response.status === NO_CONTENT) {
		return Promise.resolve(null);
	}
	return response.text().then((text) => (text ? JSON.parse(text) : null));
};

// disabling this for these functions because otherwise it breaks syntax highlighting, it doesn't
// actually change the type that is used
/* oxlint-disable typescript/no-unnecessary-type-constraint */

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const applyErrorHandling = <ResponseType extends any = any>(
	responsePromise: Promise<Response>,
): Promise<ResponseType> =>
	responsePromise.then(handleErrors).then(processResponseFromServer).catch(handleNetworkErrors);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
interface RequestRetryOptions<ResponseType = any> {
	retryPredicate?: RetryOptions<ResponseType>['retryPredicate'];
	retryAttempts?: RetryOptions<ResponseType>['retryAttempts'];
	onRetry?: RetryOptions<ResponseType>['onRetry'];
}

type Options<ResponseType> = RequestInit & RequestRetryOptions<ResponseType>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performRequestWithRetry = <ResponseType extends any = any>(
	url: string,
	options: Options<ResponseType>,
): Promise<ResponseType> => {
	const { retryPredicate, retryAttempts, onRetry, ...requestOptions } = options;
	const executeRequest = () =>
		applyErrorHandling<ResponseType>(
			fetch(getReroutableURL(url), applyObservabilityHeaders(url, requestOptions)),
		);

	if (killswitch('platform_fetch_retries')) {
		return executeRequest();
	}

	return retryOnError<ResponseType>(executeRequest, {
		retryFunc: executeRequest,
		retryPredicate,
		retryAttempts,
		onRetry,
	});
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performGetRequest = <ResponseType extends any = any>(
	url: string,
	options: RequestInit = {},
): Promise<ResponseType> =>
	fetch(getReroutableURL(url), applyObservabilityHeaders(url, { ...generalOptions, ...options }))
		.then(handleErrors)
		.then(processResponseFromServer)
		.catch(handleNetworkErrors);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performPutRequest = <ResponseType extends any = any>(
	url: string,
	options: RequestInit = {},
): Promise<ResponseType> =>
	fetch(getReroutableURL(url), applyObservabilityHeaders(url, { ...putOptions, ...options }))
		.then(handleErrors)
		.then(processResponseFromServer)
		.catch(handleNetworkErrors);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performPostRequest = <ResponseType extends any = any>(
	url: string,
	options: RequestInit = {},
): Promise<ResponseType> =>
	fetch(getReroutableURL(url), applyObservabilityHeaders(url, { ...postOptions, ...options }))
		.then(handleErrors)
		.then(processResponseFromServer)
		.catch(handleNetworkErrors);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performDeleteRequest = <ResponseType extends any = any>(
	url: string,
	options: RequestInit = {},
): Promise<ResponseType> =>
	fetch(getReroutableURL(url), applyObservabilityHeaders(url, { ...deleteOptions, ...options }))
		.then(handleErrors)
		.then(processResponseFromServer)
		.catch(handleNetworkErrors);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performPatchRequest = <ResponseType extends any = any>(
	url: string,
	options: RequestInit = {},
): Promise<ResponseType> =>
	fetch(getReroutableURL(url), applyObservabilityHeaders(url, { ...patchOptions, ...options }))
		.then(handleErrors)
		.then(processResponseFromServer)
		.catch(handleNetworkErrors);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performGetRequestWithRetry = <ResponseType extends any = any>(
	url: string,
	options?: Options<ResponseType>,
): Promise<ResponseType> =>
	performRequestWithRetry<ResponseType>(url, { ...generalOptions, ...options });

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performPutRequestWithRetry = <ResponseType extends any = any>(
	url: string,
	options?: Options<ResponseType>,
): Promise<ResponseType> =>
	performRequestWithRetry<ResponseType>(url, { ...putOptions, ...options });

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performPostRequestWithRetry = <ResponseType extends any = any>(
	url: string,
	options?: Options<ResponseType>,
): Promise<ResponseType> =>
	performRequestWithRetry<ResponseType>(url, { ...postOptions, ...options });

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performDeleteRequestWithRetry = <ResponseType extends any = any>(
	url: string,
	options?: Options<ResponseType>,
): Promise<ResponseType> =>
	performRequestWithRetry<ResponseType>(url, { ...deleteOptions, ...options });

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const performPatchRequestWithRetry = <ResponseType extends any = any>(
	url: string,
	options?: Options<ResponseType>,
): Promise<ResponseType> =>
	performRequestWithRetry<ResponseType>(url, { ...patchOptions, ...options });
/* oxlint-enable typescript/no-unnecessary-type-constraint */
